import {
  Component,
  ChangeDetectionStrategy,
  input,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  inject,
  Renderer2,
  RendererStyleFlags2,
} from '@angular/core';
import { OverlayService } from '../fc-overlay/overlay.service';
import { generateRandomString } from '@fc-shared/utils/data.utils';
import { TooltipInlineComponent } from './tooltip.inline.component';

@Component({
  selector: 'fc-tooltip',
  host: {
    popover: 'manual',
    class: 'fc-overlay',
    '[attr.data-position]': 'position()',
    '[attr.role]': '"tooltip"',
  },
  template: `
    <fc-tooltip-inline [position]="position()" [showArrow]="showArrow()">
      <ng-content></ng-content>
    </fc-tooltip-inline>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipInlineComponent],
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
  showArrow = input<boolean>(true);
  position = input<'top' | 'bottom' | 'left' | 'right'>('top');
  target = input<HTMLElement | ElementRef<HTMLElement>>();
  offset = input<number>(8);

  private elementRef = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private overlayService = inject(OverlayService);
  private overlayId = `fc-tooltip-${generateRandomString(6)}`;

  private mouseEnterHandler = () => this.show();
  private mouseLeaveHandler = () => this.hide();
  private focusHandler = () => this.onFocus();
  private blurHandler = () => this.hide();
  private onDocumentClick = (): void => {
    const origin = this.getOriginElement();
    if (!origin?.matches(':hover')) {
      this.hide();
    }
  };

  ngAfterViewInit(): void {
    const origin = this.getOriginElement();
    if (!origin) return;

    this.renderer.setStyle(
      origin,
      'anchor-name',
      `--${this.overlayId}`,
      RendererStyleFlags2.DashCase,
    );

    const el = this.elementRef.nativeElement;
    this.renderer.setAttribute(el, 'id', this.overlayId);
    this.renderer.setStyle(
      el,
      'position-anchor',
      `--${this.overlayId}`,
      RendererStyleFlags2.DashCase,
    );
    this.renderer.setStyle(
      el,
      '--fc-overlay-offset',
      `${this.offset()}px`,
      RendererStyleFlags2.DashCase,
    );
    this.overlayService.register(this.overlayId, el);

    this.bindOriginEvents(origin);
  }

  ngOnDestroy(): void {
    const origin = this.getOriginElement();
    if (origin) {
      origin.removeEventListener('mouseenter', this.mouseEnterHandler);
      origin.removeEventListener('mouseleave', this.mouseLeaveHandler);
      origin.removeEventListener('focus', this.focusHandler, true);
      origin.removeEventListener('blur', this.blurHandler, true);
    }
    this.overlayService.unregister(this.overlayId);
  }

  private getOriginElement(): HTMLElement | null {
    const t = this.target();
    if (!t) return null;
    return t instanceof ElementRef ? (t.nativeElement as HTMLElement) : t;
  }

  private bindOriginEvents(origin: HTMLElement): void {
    origin.addEventListener('mouseenter', this.mouseEnterHandler);
    origin.addEventListener('mouseleave', this.mouseLeaveHandler);
    origin.addEventListener('focus', this.focusHandler, true);
    origin.addEventListener('blur', this.blurHandler, true);
  }

  private show(): void {
    this.elementRef.nativeElement.showPopover();
    document.addEventListener('click', this.onDocumentClick, true);
  }

  private hide(): void {
    document.removeEventListener('click', this.onDocumentClick, true);
    try {
      this.elementRef.nativeElement.hidePopover();
    } catch {
      /* already hidden */
    }
  }

  private onFocus(): void {
    const origin = this.getOriginElement();
    if (origin?.matches(':focus-visible')) {
      this.show();
    }
  }
}
